import { defineStore } from 'pinia';
import { type Component, computed, reactive, ref, shallowRef } from 'vue';
import { useRoute } from 'vue-router';

import moment from 'moment-timezone';

import { useDefaultToast } from '@/composables/useDefaultToast';
import { ROUTES } from '@/constants/routes';
import { createLatestRequestGuard } from '@/helpers/latestRequest';
import { getLocalStorage } from '@/helpers/storage';
import i18n from '@/i18n';
import { useAuthStore } from '@/modules/auth/store/authStore';
import { clientService } from '@/modules/clients/services/clientService';
import {
	extractAvailabilities,
	extractCities,
	extractContractTypes,
	extractDistanceKm,
	extractExcludedQualification,
	extractExperienceLevels,
	extractHabilitations,
	extractHasOngoingProposal,
	extractKeywords,
	extractLastUpdate,
	extractOrigins,
	extractQualification,
	extractWeekEndRequired,
	extractWorkerTypes,
} from '@/modules/companyNeed/helpers/filterExtractors';
import {
	fetchMissionWhenProjected,
	isCountedMissionStatus,
} from '@/modules/companyNeed/helpers/mission';
import { upsertWorkerInterview } from '@/modules/companyNeed/helpers/workerInterview';
import companyNeedService from '@/modules/companyNeed/services/companyNeed';
import missionService from '@/modules/companyNeed/services/missionService';
import { useMissionPatchStore } from '@/modules/companyNeed/store/missionPatchStore';
import {
	CompanyNeedLoadingState,
	RangeType,
	UnavailabilityType,
} from '@/modules/companyNeed/types';
import { HistoryType } from '@/modules/companyNeed/types/history';
import { MoveWorkerCompanyNeedCommands } from '@/modules/companyNeed/types/moveWorker';
import { CompanyNeedWorkerStatus } from '@/modules/companyNeed/types/worker';
import { DetailsView } from '@/modules/companyNeed/views/details/types';
import { JobOfferStatus } from '@/types/jobOffer';
import { WorkerMissionStatus } from '@/types/mission';

import type { ActiveFilter } from '@/dsComponents/FilterDropdown/types';
import type {
	CompanyNeed,
	CompanyNeedPaginatedResponse,
	CompanyNeedReloadSection,
	CompanyNeedWorkersResponse,
	JobOfferDetails,
	RecruitmentWorker,
	Unavailability,
	WorkerContact,
	WorkerInterview,
} from '@/modules/companyNeed/types';
import type { RepublishJobOfferResponse } from '@/modules/companyNeed/types/joboffer';
import type { MissionBulkWorker } from '@/modules/companyNeed/types/missions';
import type { MoveWorker } from '@/modules/companyNeed/types/moveWorker';
import type {
	CandidateSortBy,
	CandidatesQueryParams,
	CandidateWorker,
} from '@/modules/companyNeed/views/details/candidates/types';
import type {
	AvailabilityHint,
	MatchingWorker,
	MatchingWorkerItemDto,
	MatchingWorkersMetadata,
	MatchingWorkersMetadataQueryParams,
	MatchingWorkersQueryParams,
	MatchingWorkersSortBy,
} from '@/modules/companyNeed/views/details/matching/types';
import type {
	MissionList,
	MissionStatus,
} from '@/modules/companyNeed/views/details/missions/types';
import type {
	PoolWorkersQueryParams,
	WorkerPool,
} from '@/modules/companyNeed/views/details/pool/types';
import type { RejectedProfileQuery } from '@/modules/companyNeed/views/details/rejected-profiles/types';
import type { SortOrder } from '@/types/common';
import type { Contact } from '@/types/contact';

import { FilterCategoryType } from '@/dsComponents/FilterDropdown/types';

export { CompanyNeedFilterCategoryId } from '@/modules/companyNeed/helpers/filterExtractors';

// ─── Module-level constants & helpers ────────────────────────────────────────

const DEFAULT_MATCHING_LIMIT = 25;
const DEFAULT_MATCHING_OFFSET = 0;
const DEFAULT_CANDIDATES_LIMIT = 25;
const DEFAULT_CANDIDATES_OFFSET = 0;
const DEFAULT_POOL_LIMIT = 25;
const DEFAULT_POOL_OFFSET = 0;

const MATCHING_STORAGE_KEY_PREFIX = 'company-need-matching-filters';
const POOL_STORAGE_KEY_PREFIX = 'company-need-pool-filters';

const createDefaultPaginatedMatchingWorkers =
	(): CompanyNeedPaginatedResponse<MatchingWorker> => ({
		items: [],
		offset: 1,
		total: 0,
		limit: DEFAULT_MATCHING_LIMIT,
		page: 1,
		hasNextPage: false,
		hasPreviousPage: false,
		totalPages: 0,
	});

const createDefaultPaginatedPoolWorkers =
	(): CompanyNeedPaginatedResponse<WorkerPool> => ({
		items: [],
		offset: 1,
		total: 0,
		limit: DEFAULT_POOL_LIMIT,
		page: 1,
		hasNextPage: false,
		hasPreviousPage: false,
		totalPages: 0,
	});

const createEmptyRecruitmentProfiles = (): CompanyNeedWorkersResponse => ({
	prequalified: [],
	contacted: [],
	interview: [],
	proposedToCompany: [],
	interviewCompany: [],
	missionProposed: [],
});

const RECRUITMENT_STATUS_KEYS = [
	'prequalified',
	'contacted',
	'interview',
	'proposedToCompany',
	'interviewCompany',
	'missionProposed',
] as const satisfies readonly (keyof CompanyNeedWorkersResponse)[];

type RecruitmentStatusKey = (typeof RECRUITMENT_STATUS_KEYS)[number];

const WORKER_STATUS_TO_RECRUITMENT_KEY: Partial<
	Record<CompanyNeedWorkerStatus, RecruitmentStatusKey>
> = {
	[CompanyNeedWorkerStatus.PREQUALIFIED]: 'prequalified',
	[CompanyNeedWorkerStatus.CONTACTED]: 'contacted',
	[CompanyNeedWorkerStatus.INTERVIEW]: 'interview',
	[CompanyNeedWorkerStatus.COMPANY_PROPOSED]: 'proposedToCompany',
	[CompanyNeedWorkerStatus.COMPANY_INTERVIEW]: 'interviewCompany',
	[CompanyNeedWorkerStatus.MISSION_PROPOSED]: 'missionProposed',
};

const countRecruitmentProfiles = (
	data: CompanyNeedWorkersResponse | null
): number =>
	data
		? RECRUITMENT_STATUS_KEYS.reduce(
				(count, key) => count + data[key].length,
				0
			)
		: 0;

interface StoredFilters {
	search: string;
	activeFilters: ActiveFilter[];
	toggles?: Record<string, boolean>;
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const useCompanyNeedStore = defineStore('companyNeed', () => {
	const route = useRoute();
	const { toastError } = useDefaultToast();
	const missionPatchStore = useMissionPatchStore();

	// ─── State ───────────────────────────────────────────────────────────────

	// Core
	const actionBarSlot = shallowRef<
		{ component: Component; props?: Record<string, unknown> } | undefined
	>(undefined);
	const loadingStates = reactive({
		[CompanyNeedLoadingState.COMPANY_NEED]: false,
		[CompanyNeedLoadingState.MATCHING_WORKERS]: false,
		[CompanyNeedLoadingState.POOL_WORKERS]: false,
		[CompanyNeedLoadingState.REJECTED_PROFILES]: false,
		[CompanyNeedLoadingState.CANDIDATES]: false,
		[CompanyNeedLoadingState.MISSIONS]: false,
		[CompanyNeedLoadingState.RECRUITMENT]: false,
		[CompanyNeedLoadingState.COMPANY_CONTACTS]: false,
	});
	const companyNeedData = ref<CompanyNeed | null>(null);
	const companyContacts = ref<Contact[]>([]);

	// Matching
	const matchingWorkersSearch = ref('');
	const matchingWorkersFilters = ref<ActiveFilter[]>([]);
	const isMatchingAvailableOnly = ref(false);
	const isMatchingJobOfferResponseOnly = ref(false);
	const isMatchingFlexibleEnabled = ref(false);
	const isMatchingFlexibleAvailable = ref(false);
	const isMatchingFlexibleLoading = ref(false);
	const matchingWorkersSortBy = ref<MatchingWorkersSortBy | undefined>();
	const matchingWorkersSortOrder = ref<SortOrder | undefined>();
	const matchingWorkersLimit = ref(DEFAULT_MATCHING_LIMIT);
	const paginatedMatchingWorkers = ref<
		CompanyNeedPaginatedResponse<MatchingWorker>
	>(createDefaultPaginatedMatchingWorkers());
	const matchingWorkersMetadata = ref<MatchingWorkersMetadata>({
		keywords: [],
		cities: [],
	});

	// Pool
	const poolWorkersSearch = ref('');
	const poolWorkersFilters = ref<ActiveFilter[]>([]);
	const isPoolAvailableOnly = ref(false);
	const poolWorkersLimit = ref(DEFAULT_POOL_LIMIT);
	const paginatedPoolWorkers = ref<CompanyNeedPaginatedResponse<WorkerPool>>(
		createDefaultPaginatedPoolWorkers()
	);

	// Rejected profiles
	const rejectedProfiles = ref<RejectedProfileQuery[]>([]);

	// Missions
	const missions = ref<MissionList[]>([]);
	const missionsSearch = ref('');
	const missionsStatusFilter = ref<MissionStatus[]>([]);
	/** Proposed quantity bump when a worker was added before the mission row existed in `missions` (e.g. list not loaded yet). */
	const orphanProposedAdds = ref<Record<string, number>>({});
	/** Temporary shift of the missions tab count after optimistic creations or status changes (cleared on company need refetch). */
	const optimisticMissionsDelta = ref(0);
	/** Ids of optimistic missions inserted locally, awaiting reconciliation from the command-side response. */
	const optimisticMissionIds = ref<Set<string>>(new Set());
	const missionsRequests = createLatestRequestGuard();

	// Candidates
	const candidates = ref<CandidateWorker[]>([]);
	const candidatesSearch = ref('');
	const candidatesFilters = ref<ActiveFilter[]>([]);
	const candidatesLimit = ref(DEFAULT_CANDIDATES_LIMIT);
	const candidatesPage = ref(DEFAULT_CANDIDATES_OFFSET);
	const candidatesTotal = ref(0);
	const candidatesSortBy = ref<CandidateSortBy | undefined>();
	const candidatesSortOrder = ref<SortOrder | undefined>();

	// Recruitment – optimistic state
	const recruitmentProfiles = ref<CompanyNeedWorkersResponse | null>(null);
	const optimisticMatchingRemovedWorkerIds = ref<string[]>([]);
	/** Worker ids hidden from the candidates list until the next fetch confirms server state. */
	const optimisticCandidatesRemovedWorkerIds = ref<string[]>([]);
	const optimisticRecruitmentTargetCount = ref<number | null>(null);
	const optimisticRecruitmentWorkers = ref<
		Record<
			string,
			{ worker: RecruitmentWorker; statusKey: RecruitmentStatusKey }
		>
	>({});
	const optimisticRejectedProfiles = ref<Record<string, RejectedProfileQuery>>(
		{}
	);
	/** Worker ids hidden from recruitment lists until the next fetch confirms server state. */
	const optimisticRecruitmentRemovedWorkerIds = ref<string[]>([]);
	/** Temporary bump to the pool tab count after moving workers to the pool (cleared on company need refetch). */
	const optimisticPoolProfileDelta = ref(0);
	const pendingPoolWorkers = ref<Record<string, WorkerPool[]>>({});

	// ─── Getters ─────────────────────────────────────────────────────────────

	// Core
	const companyNeedId = computed(() => route.params.companyNeedId as string);
	const isCompanyNeedSynced = computed(
		() => companyNeedId.value === companyNeedData.value?._id
	);
	const companyNeed = computed(() =>
		isCompanyNeedSynced.value ? companyNeedData.value : null
	);
	const companyNeedCompanyId = computed(
		() => companyNeed.value?.companyId ?? null
	);
	const hasJobOffer = computed(() => !!companyNeed.value?.jobOffer);

	const getLoadingState = (state: CompanyNeedLoadingState) =>
		loadingStates[state];

	// Matching filter extraction
	const matchingDistanceKm = computed(() =>
		extractDistanceKm(matchingWorkersFilters.value)
	);
	const matchingLastUpdate = computed(() =>
		extractLastUpdate(matchingWorkersFilters.value)
	);
	const matchingContractTypes = computed(() =>
		extractContractTypes(matchingWorkersFilters.value)
	);
	const matchingExperienceLevels = computed(() =>
		extractExperienceLevels(matchingWorkersFilters.value)
	);
	const matchingQualification = computed(() =>
		extractQualification(matchingWorkersFilters.value)
	);
	const matchingExcludedQualification = computed(() =>
		extractExcludedQualification(matchingWorkersFilters.value)
	);
	const matchingHabilitations = computed(() =>
		extractHabilitations(matchingWorkersFilters.value)
	);
	const matchingAvailabilities = computed(() =>
		extractAvailabilities(matchingWorkersFilters.value)
	);
	const matchingWeekEndRequired = computed(() =>
		extractWeekEndRequired(matchingWorkersFilters.value)
	);
	const matchingKeywords = computed(() =>
		extractKeywords(matchingWorkersFilters.value)
	);
	const matchingCities = computed(() =>
		extractCities(matchingWorkersFilters.value)
	);
	const matchingWorkerTypes = computed(() =>
		extractWorkerTypes(matchingWorkersFilters.value)
	);
	const matchingHasOngoingProposal = computed(() =>
		extractHasOngoingProposal(matchingWorkersFilters.value)
	);

	// Pool filter extraction
	const poolQualification = computed(() =>
		extractQualification(poolWorkersFilters.value)
	);
	const poolExcludedQualification = computed(() =>
		extractExcludedQualification(poolWorkersFilters.value)
	);
	const poolDistanceKm = computed(() =>
		extractDistanceKm(poolWorkersFilters.value)
	);
	const poolLastUpdate = computed(() =>
		extractLastUpdate(poolWorkersFilters.value)
	);
	const poolContractTypes = computed(() =>
		extractContractTypes(poolWorkersFilters.value)
	);
	const poolExperienceLevels = computed(() =>
		extractExperienceLevels(poolWorkersFilters.value)
	);
	const poolHasOngoingProposal = computed(() =>
		extractHasOngoingProposal(poolWorkersFilters.value)
	);

	// Candidates filter extraction
	const candidatesQualification = computed(() =>
		extractQualification(candidatesFilters.value)
	);
	const candidatesOrigins = computed(() =>
		extractOrigins(candidatesFilters.value)
	);

	// Derived data
	const visibleMatchingWorkers = computed<
		CompanyNeedPaginatedResponse<MatchingWorker>
	>(() => {
		const hiddenIds = new Set(optimisticMatchingRemovedWorkerIds.value);
		if (hiddenIds.size === 0) {
			return paginatedMatchingWorkers.value;
		}

		const visibleData = paginatedMatchingWorkers.value.items.filter(
			(worker) => !hiddenIds.has(worker.id)
		);
		const removedCount =
			paginatedMatchingWorkers.value.items.length - visibleData.length;
		return {
			...paginatedMatchingWorkers.value,
			items: visibleData,
			total: Math.max(0, paginatedMatchingWorkers.value.total - removedCount),
		};
	});

	const visibleCandidates = computed<CandidateWorker[]>(() => {
		const hiddenIds = new Set(optimisticCandidatesRemovedWorkerIds.value);
		if (hiddenIds.size === 0) return candidates.value;
		return candidates.value.filter((candidate) => !hiddenIds.has(candidate.id));
	});

	const visibleCandidatesTotal = computed(() => {
		const hiddenIds = new Set(optimisticCandidatesRemovedWorkerIds.value);
		if (hiddenIds.size === 0) return candidatesTotal.value;
		const removedOnPage = candidates.value.filter((candidate) =>
			hiddenIds.has(candidate.id)
		).length;
		return Math.max(0, candidatesTotal.value - removedOnPage);
	});

	const visibleRejectedProfiles = computed<RejectedProfileQuery[]>(() => {
		const optimisticEntries = Object.values(optimisticRejectedProfiles.value);
		if (optimisticEntries.length === 0) return rejectedProfiles.value;

		const serverIds = new Set(
			rejectedProfiles.value.map((profile) => profile.id)
		);
		const pending = optimisticEntries.filter(
			(profile) => !serverIds.has(profile.id)
		);
		if (pending.length === 0) return rejectedProfiles.value;

		return [...pending, ...rejectedProfiles.value];
	});

	const hasActivePoolQuery = computed(
		() =>
			!!poolWorkersSearch.value ||
			poolWorkersFilters.value.length > 0 ||
			isPoolAvailableOnly.value ||
			paginatedPoolWorkers.value.offset > 1
	);

	const visiblePoolWorkers = computed<CompanyNeedPaginatedResponse<WorkerPool>>(
		() => {
			const pending = pendingPoolWorkers.value[companyNeedId.value] ?? [];
			if (pending.length === 0 || hasActivePoolQuery.value) {
				return paginatedPoolWorkers.value;
			}

			const serverIds = new Set(
				paginatedPoolWorkers.value.items.map((worker) => worker.workerId)
			);
			const stillPending = pending.filter(
				(worker) => !serverIds.has(worker.workerId)
			);
			if (stillPending.length === 0) return paginatedPoolWorkers.value;

			return {
				...paginatedPoolWorkers.value,
				items: [...stillPending, ...paginatedPoolWorkers.value.items],
				total: paginatedPoolWorkers.value.total + stillPending.length,
			};
		}
	);

	const mergedRecruitmentProfiles = computed<CompanyNeedWorkersResponse | null>(
		() => {
			const optimisticEntries = Object.values(
				optimisticRecruitmentWorkers.value
			);
			if (!recruitmentProfiles.value && optimisticEntries.length === 0) {
				return null;
			}

			const source =
				recruitmentProfiles.value ?? createEmptyRecruitmentProfiles();
			const optimisticIds = new Set(
				optimisticEntries.map(({ worker }) => worker.workerId)
			);
			const removedIds = new Set(optimisticRecruitmentRemovedWorkerIds.value);
			const merged = RECRUITMENT_STATUS_KEYS.reduce((acc, key) => {
				acc[key] = source[key]
					.filter((worker) => !optimisticIds.has(worker.workerId))
					.filter((worker) => !removedIds.has(worker.workerId));
				return acc;
			}, createEmptyRecruitmentProfiles());

			for (const { worker, statusKey } of optimisticEntries) {
				if (
					merged[statusKey].some(
						(currentWorker) => currentWorker.workerId === worker.workerId
					)
				)
					continue;
				merged[statusKey].unshift(worker);
			}

			return merged;
		}
	);

	const recruitmentProfilesCount = computed(() => {
		const visibleCount = countRecruitmentProfiles(
			mergedRecruitmentProfiles.value
		);
		const optimisticCount = optimisticRecruitmentTargetCount.value ?? 0;

		return Math.max(visibleCount, optimisticCount);
	});

	const counts = computed<Partial<Record<DetailsView, number>>>(() => ({
		[DetailsView.MATCHING]: visibleMatchingWorkers.value.total,
		[DetailsView.CANDIDATES]: getLoadingState(
			CompanyNeedLoadingState.CANDIDATES
		)
			? 0
			: visibleCandidatesTotal.value,
		[DetailsView.RECRUITMENT]: recruitmentProfilesCount.value,
		[DetailsView.POOL]: Math.max(
			(companyNeed.value?.poolProfileNumber ?? 0) +
				optimisticPoolProfileDelta.value,
			visiblePoolWorkers.value.total
		),
		[DetailsView.MISSIONS]: Math.max(
			0,
			(companyNeed.value?.missions ?? 0) + optimisticMissionsDelta.value
		),
		[DetailsView.REJECTED_PROFILES]: Math.max(
			companyNeed.value?.refusedProfileNumber ?? 0,
			visibleRejectedProfiles.value.length
		),
	}));

	// ─── Internal helpers ────────────────────────────────────────────────────

	const setLoadingState = (state: CompanyNeedLoadingState, value: boolean) => {
		loadingStates[state] = value;
	};

	const getMissionListProposedQuantity = (mission: MissionList): number => {
		const entry = mission.workersSelection.find(
			(ws) => ws.workerStatus === WorkerMissionStatus.PROPOSED
		);
		return entry?.quantity ?? 0;
	};

	const setMissionListProposedQuantity = (
		mission: MissionList,
		proposedQty: number
	): void => {
		const idx = mission.workersSelection.findIndex(
			(ws) => ws.workerStatus === WorkerMissionStatus.PROPOSED
		);
		if (proposedQty <= 0) {
			if (idx >= 0) {
				mission.workersSelection.splice(idx, 1);
			}
			return;
		}
		if (idx >= 0) {
			mission.workersSelection[idx] = {
				workerStatus: WorkerMissionStatus.PROPOSED,
				quantity: proposedQty,
			};
		} else {
			mission.workersSelection.push({
				workerStatus: WorkerMissionStatus.PROPOSED,
				quantity: proposedQty,
			});
		}
	};

	const mergeMissionsListWithLocalState = (
		fresh: MissionList[]
	): MissionList[] => {
		const merged = fresh.map((fm) => {
			const oldM = missions.value.find((m) => m.id === fm.id);
			const pFresh = getMissionListProposedQuantity(fm);
			const pOld = oldM ? getMissionListProposedQuantity(oldM) : 0;
			const orphan = !oldM ? (orphanProposedAdds.value[fm.id] ?? 0) : 0;
			const pTarget = Math.max(pFresh, pOld, orphan);

			if (pTarget === pFresh) {
				if (!oldM && orphan > 0 && pFresh >= orphan) {
					const next = { ...orphanProposedAdds.value };
					delete next[fm.id];
					orphanProposedAdds.value = next;
				}
				return fm;
			}

			const nextMission: MissionList = {
				...fm,
				workersSelection: fm.workersSelection.map((ws) => ({ ...ws })),
			};
			setMissionListProposedQuantity(nextMission, pTarget);
			if (!oldM && orphan > 0) {
				const next = { ...orphanProposedAdds.value };
				delete next[fm.id];
				orphanProposedAdds.value = next;
			}
			return nextMission;
		});

		if (optimisticMissionIds.value.size === 0) return merged;

		const freshIds = new Set(fresh.map((m) => m.id));
		const localById = new Map(missions.value.map((m) => [m.id, m]));
		const nextIds = new Set<string>();
		const stillPending: MissionList[] = [];
		for (const id of optimisticMissionIds.value) {
			if (freshIds.has(id)) continue;
			const localMission = localById.get(id);
			if (!localMission) continue;
			nextIds.add(id);
			stillPending.push(localMission);
		}
		optimisticMissionIds.value = nextIds;
		return stillPending.length > 0 ? [...stillPending, ...merged] : merged;
	};

	const withLoading = async (
		loadingState: CompanyNeedLoadingState,
		fetcher: () => Promise<void>,
		errorKey: string
	): Promise<void> => {
		if (getLoadingState(loadingState)) return;
		setLoadingState(loadingState, true);
		try {
			await fetcher();
		} catch {
			toastError(i18n.global.t(errorKey));
		} finally {
			setLoadingState(loadingState, false);
		}
	};

	const mapAvailabilityHintToUnavailability = (
		hint: AvailabilityHint
	): Unavailability | undefined => {
		if (hint.kind === 'available') return undefined;
		return {
			unvailabilityType: UnavailabilityType.DEFAULT,
			rangeType:
				hint.kind === 'unavailable_until' ? RangeType.TO : RangeType.FROM,
			date: moment.utc(hint.date).startOf('day').valueOf(),
		};
	};

	const formatMatchingWorkers = (
		matchingWorkers: MatchingWorkerItemDto[],
		isSuggested: boolean
	): MatchingWorker[] => {
		return matchingWorkers.map((worker) => ({
			id: worker.talentId,
			firstname: worker.firstname,
			lastname: worker.lastname,
			fromJobOffer: worker.fromJobOffer,
			inPool: worker.inPool,
			lastUpdate: worker.lastUpdate ? new Date(worker.lastUpdate).getTime() : 0,
			phone: worker.phone,
			email: worker.email ?? '',
			distance: worker.distance,
			city: worker.city ?? null,
			missionsWithClient: worker.missionsWithClient ?? 0,
			missionsWithAgency: worker.missionsWithAgency ?? 0,
			registered: false,
			isSuggested,
			unavailability: mapAvailabilityHintToUnavailability(worker.availability),
			ongoingProposals: worker.ongoingProposals ?? [],
			availableCommands: Object.values(MoveWorkerCompanyNeedCommands).filter(
				(command) =>
					command !== MoveWorkerCompanyNeedCommands.ADD_TO_OTHER_POOLS
			),
		}));
	};

	const mapMoveWorkerToRecruitmentWorker = (
		worker: MoveWorker
	): RecruitmentWorker => ({
		workerId: worker.id,
		firstname: worker.firstname,
		lastname: worker.lastname,
		email: worker.email ?? null,
		phone: worker.phone ?? '',
		picture: worker.picture,
		postulatedToJobOffer: worker.fromJobOffer ?? false,
		following: worker.history ?? [],
		contact: null,
		interview: null,
	});

	const isWorkerInRecruitmentReadModel = (workerId: string): boolean => {
		const data = recruitmentProfiles.value;
		if (!data) return false;
		return RECRUITMENT_STATUS_KEYS.some((key) =>
			data[key].some((worker) => worker.workerId === workerId)
		);
	};

	const buildRecruitmentWorkerForMissionProposedFromBulk = (
		worker: MissionBulkWorker,
		mission: { id: string; name: string; status: MissionStatus }
	): RecruitmentWorker => ({
		workerId: worker.workerId,
		firstname: worker.firstname,
		lastname: worker.lastname,
		email: worker.email ?? null,
		phone: worker.phone ?? '',
		picture: null,
		postulatedToJobOffer: false,
		following: [
			{
				type: HistoryType.MISSION_PROPOSED,
				date: new Date(),
			},
		],
		contact: null,
		interview: null,
		missions: [
			{
				missionId: mission.id,
				missionName: mission.name,
				missionStatus: mission.status,
				workerStatus: WorkerMissionStatus.PROPOSED,
				availableStatuses: [],
			},
		],
	});

	const applyOptimisticRecruitmentMissionProposedFromPool = (
		worker: MissionBulkWorker,
		mission: { id: string; name: string; status: MissionStatus }
	) => {
		let nextRecruitmentTargetCount: number | null = null;
		if (!isWorkerInRecruitmentReadModel(worker.workerId)) {
			const currentCount = recruitmentProfilesCount.value;
			nextRecruitmentTargetCount = currentCount + 1;
		}

		const recruitmentWorker = buildRecruitmentWorkerForMissionProposedFromBulk(
			worker,
			mission
		);
		const nextOptimisticWorkers = { ...optimisticRecruitmentWorkers.value };
		nextOptimisticWorkers[worker.workerId] = {
			worker: recruitmentWorker,
			statusKey: 'missionProposed',
		};
		optimisticRecruitmentWorkers.value = nextOptimisticWorkers;

		if (nextRecruitmentTargetCount !== null) {
			optimisticRecruitmentTargetCount.value = nextRecruitmentTargetCount;
		}
	};

	// Reconciliation – clears optimistic state once the server confirms
	const reconcileOptimisticRecruitmentWorkers = (
		data: CompanyNeedWorkersResponse | null
	) => {
		if (!data || Object.keys(optimisticRecruitmentWorkers.value).length === 0)
			return;

		const readModelWorkerIds = new Set(
			RECRUITMENT_STATUS_KEYS.flatMap((key) =>
				data[key].map((worker) => worker.workerId)
			)
		);
		const remainingWorkers = Object.fromEntries(
			Object.entries(optimisticRecruitmentWorkers.value).filter(
				([workerId]) => !readModelWorkerIds.has(workerId)
			)
		);

		if (
			Object.keys(remainingWorkers).length !==
			Object.keys(optimisticRecruitmentWorkers.value).length
		) {
			optimisticRecruitmentWorkers.value = remainingWorkers;
		}

		if (Object.keys(optimisticRecruitmentWorkers.value).length === 0) {
			optimisticRecruitmentTargetCount.value = null;
		}
	};

	const removePendingPoolWorkers = (
		needId: string,
		shouldRemove: (worker: WorkerPool) => boolean
	) => {
		const pending = pendingPoolWorkers.value[needId];
		if (!pending?.length) return;
		const remaining = pending.filter((worker) => !shouldRemove(worker));
		if (remaining.length === pending.length) return;

		const next = { ...pendingPoolWorkers.value };
		if (remaining.length > 0) {
			next[needId] = remaining;
		} else {
			delete next[needId];
		}
		pendingPoolWorkers.value = next;
	};

	const reconcilePendingPoolWorkers = (needId: string, items: WorkerPool[]) => {
		const serverIds = new Set(items.map((worker) => worker.workerId));
		removePendingPoolWorkers(needId, (worker) =>
			serverIds.has(worker.workerId)
		);
	};

	const reconcileOptimisticRejectedProfiles = (
		data: RejectedProfileQuery[]
	) => {
		if (Object.keys(optimisticRejectedProfiles.value).length === 0) return;
		const serverIds = new Set(data.map((profile) => profile.id));
		const remaining = Object.fromEntries(
			Object.entries(optimisticRejectedProfiles.value).filter(
				([workerId]) => !serverIds.has(workerId)
			)
		);
		if (
			Object.keys(remaining).length !==
			Object.keys(optimisticRejectedProfiles.value).length
		) {
			optimisticRejectedProfiles.value = remaining;
		}
	};

	const reconcileOptimisticRecruitmentRemovals = (
		data: CompanyNeedWorkersResponse | null
	) => {
		if (!data || optimisticRecruitmentRemovedWorkerIds.value.length === 0)
			return;
		const presentIds = new Set(
			RECRUITMENT_STATUS_KEYS.flatMap((key) =>
				data[key].map((worker) => worker.workerId)
			)
		);
		const remaining = optimisticRecruitmentRemovedWorkerIds.value.filter((id) =>
			presentIds.has(id)
		);
		if (
			remaining.length !== optimisticRecruitmentRemovedWorkerIds.value.length
		) {
			optimisticRecruitmentRemovedWorkerIds.value = remaining;
		}
	};

	// Matching – flexible pagination helpers
	const matchingWorkersRequests = createLatestRequestGuard();
	let lastMatchingMetadataParamsKey: string | null = null;

	const getMatchingPagination = (params: MatchingWorkersQueryParams) => {
		const pageLimit =
			params.limit ?? matchingWorkersLimit.value ?? DEFAULT_MATCHING_LIMIT;
		const startRow = params.offset ?? 0;
		const pageIdx = Math.floor(startRow / pageLimit);
		return { pageIdx, pageLimit, startRow };
	};

	const getClassicAndFlexibleSlices = (
		classicResponse: CompanyNeedPaginatedResponse<MatchingWorkerItemDto>,
		startRow: number,
		pageLimit: number
	) => {
		const classicCount = classicResponse.items.length;
		const classicSlice = classicResponse.items.slice(
			startRow,
			Math.min(startRow + pageLimit, classicCount)
		);
		const flexStartRow = Math.max(0, startRow - classicCount);
		const flexLimit = pageLimit - classicSlice.length;
		return { classicSlice, flexStartRow, flexLimit };
	};

	const fetchFlexibleMatchingWorkersSlice = async (
		queryParams: MatchingWorkersQueryParams,
		classicResponse: CompanyNeedPaginatedResponse<MatchingWorkerItemDto>,
		flexStartRow: number,
		flexLimit: number
	) => {
		const excludeTalentIds = classicResponse.items.map(
			(worker) => worker.talentId
		);
		const flexibleQueryBase: MatchingWorkersQueryParams = {
			...queryParams,
			excludeTalentIds,
		};

		if (flexLimit > 0) {
			return companyNeedService.fetchFlexibleMatchingWorkersForCompanyNeed(
				companyNeedId.value,
				{
					...flexibleQueryBase,
					offset: flexStartRow,
					limit: flexLimit,
				}
			);
		}

		return companyNeedService.fetchFlexibleMatchingWorkersForCompanyNeed(
			companyNeedId.value,
			{
				...flexibleQueryBase,
				offset: 0,
				limit: 1,
			}
		);
	};

	const applyFlexibleMatchingPagination = (
		classicResponse: CompanyNeedPaginatedResponse<MatchingWorkerItemDto>,
		flexibleMatchingWorkers: CompanyNeedPaginatedResponse<MatchingWorkerItemDto>,
		classicSlice: MatchingWorkerItemDto[],
		flexLimit: number,
		pageIdx: number,
		pageLimit: number,
		startRow: number
	) => {
		const formattedClassicMatchingWorkers = formatMatchingWorkers(
			classicSlice,
			false
		);
		const formattedFlexibleMatchingWorkers =
			flexLimit > 0
				? formatMatchingWorkers(flexibleMatchingWorkers.items, true)
				: [];

		const totalCombined = classicResponse.total + flexibleMatchingWorkers.total;
		const hasNextPage = startRow + pageLimit < totalCombined;
		const hasPreviousPage = pageIdx > 0;
		const totalPages = Math.ceil(totalCombined / pageLimit) || 1;

		optimisticMatchingRemovedWorkerIds.value = [];
		paginatedMatchingWorkers.value = {
			...flexibleMatchingWorkers,
			total: totalCombined,
			items: [
				...formattedClassicMatchingWorkers,
				...formattedFlexibleMatchingWorkers,
			],
			offset: pageIdx + 1,
			limit: pageLimit,
			page: pageIdx + 1,
			hasNextPage,
			hasPreviousPage,
			totalPages,
		};
	};

	const fetchMatchingWorkersFlexible = async (
		classicResponse: CompanyNeedPaginatedResponse<MatchingWorkerItemDto>,
		queryParams: MatchingWorkersQueryParams,
		params: MatchingWorkersQueryParams
	) => {
		const { pageIdx, pageLimit, startRow } = getMatchingPagination(params);
		const { classicSlice, flexStartRow, flexLimit } =
			getClassicAndFlexibleSlices(classicResponse, startRow, pageLimit);
		const flexibleMatchingWorkers = await fetchFlexibleMatchingWorkersSlice(
			queryParams,
			classicResponse,
			flexStartRow,
			flexLimit
		);

		applyFlexibleMatchingPagination(
			classicResponse,
			flexibleMatchingWorkers,
			classicSlice,
			flexLimit,
			pageIdx,
			pageLimit,
			startRow
		);
	};

	// Storage – filter persistence
	const restoreMatchingFilters = () => {
		const authStore = useAuthStore();
		const userId = authStore.currentUserId;
		if (!userId || !companyNeedId.value) return;

		const key = `${MATCHING_STORAGE_KEY_PREFIX}_${companyNeedId.value}_${userId}`;
		const stored = getLocalStorage<StoredFilters>(key);
		if (!stored?.data) return;

		matchingWorkersSearch.value = stored.data.search ?? '';
		matchingWorkersFilters.value = stored.data.activeFilters ?? [];
		isMatchingAvailableOnly.value = stored.data.toggles?.availableOnly ?? false;
		isMatchingJobOfferResponseOnly.value =
			stored.data.toggles?.jobOfferResponseOnly ?? false;
	};

	const restorePoolFilters = () => {
		const authStore = useAuthStore();
		const userId = authStore.currentUserId;
		if (!userId || !companyNeedId.value) return;

		const key = `${POOL_STORAGE_KEY_PREFIX}_${companyNeedId.value}_${userId}`;
		const stored = getLocalStorage<StoredFilters>(key);
		if (!stored?.data) return;

		const storedFilters = stored.data.activeFilters ?? [];
		poolWorkersSearch.value = stored.data.search ?? '';
		isPoolAvailableOnly.value = storedFilters.some(
			(f) => f.categoryId === 'available'
		);
		poolWorkersFilters.value = storedFilters.filter(
			(f) => f.type !== FilterCategoryType.BOOLEAN
		);
	};

	// ─── Actions ─────────────────────────────────────────────────────────────

	// UI
	const setActionBarSlot = (value: typeof actionBarSlot.value) => {
		actionBarSlot.value = value;
	};

	const setMatchingFlexibleEnabled = (enabled: boolean) => {
		if (enabled && !isMatchingFlexibleAvailable.value) return;
		isMatchingFlexibleEnabled.value = enabled;
	};

	// Reset
	const resetMatchingWorkersState = () => {
		setLoadingState(CompanyNeedLoadingState.MATCHING_WORKERS, false);
		matchingWorkersSearch.value = '';
		matchingWorkersFilters.value = [];
		isMatchingAvailableOnly.value = false;
		isMatchingJobOfferResponseOnly.value = false;
		matchingWorkersSortBy.value = undefined;
		matchingWorkersSortOrder.value = undefined;
		matchingWorkersLimit.value = DEFAULT_MATCHING_LIMIT;
		paginatedMatchingWorkers.value = createDefaultPaginatedMatchingWorkers();
		matchingWorkersMetadata.value = { keywords: [], cities: [] };
		lastMatchingMetadataParamsKey = null;
		isMatchingFlexibleAvailable.value = false;
		isMatchingFlexibleEnabled.value = false;
		isMatchingFlexibleLoading.value = false;
		optimisticMatchingRemovedWorkerIds.value = [];
	};

	const resetPoolWorkersState = () => {
		setLoadingState(CompanyNeedLoadingState.POOL_WORKERS, false);
		poolWorkersSearch.value = '';
		poolWorkersFilters.value = [];
		isPoolAvailableOnly.value = false;
		poolWorkersLimit.value = DEFAULT_POOL_LIMIT;
		paginatedPoolWorkers.value = createDefaultPaginatedPoolWorkers();
		optimisticPoolProfileDelta.value = 0;
	};

	const resetRejectedProfilesState = () => {
		setLoadingState(CompanyNeedLoadingState.REJECTED_PROFILES, false);
		rejectedProfiles.value = [];
		optimisticRejectedProfiles.value = {};
	};

	const resetMissionsState = () => {
		missionsRequests.invalidate();
		setLoadingState(CompanyNeedLoadingState.MISSIONS, false);
		missions.value = [];
		missionsSearch.value = '';
		missionsStatusFilter.value = [];
		orphanProposedAdds.value = {};
		optimisticMissionsDelta.value = 0;
		optimisticMissionIds.value = new Set();
	};

	const resetRecruitmentState = () => {
		setLoadingState(CompanyNeedLoadingState.RECRUITMENT, false);
		recruitmentProfiles.value = null;
		optimisticRecruitmentTargetCount.value = null;
		optimisticRecruitmentWorkers.value = {};
		optimisticRecruitmentRemovedWorkerIds.value = [];
	};

	const resetCompanyContactsState = () => {
		setLoadingState(CompanyNeedLoadingState.COMPANY_CONTACTS, false);
		companyContacts.value = [];
	};

	const resetDetailsState = () => {
		resetMatchingWorkersState();
		resetPoolWorkersState();
		resetRejectedProfilesState();
		resetMissionsState();
		resetRecruitmentState();
		resetCompanyContactsState();
		setLoadingState(CompanyNeedLoadingState.COMPANY_NEED, false);
		companyNeedData.value = null;
	};

	// Optimistic updates
	const applyOptimisticForNeedRejection = (
		profiles: RejectedProfileQuery[]
	) => {
		const validProfiles = profiles.filter((profile) => Boolean(profile.id));
		if (validProfiles.length === 0) return;

		const workerIds = validProfiles.map((profile) => profile.id);
		optimisticMatchingRemovedWorkerIds.value = Array.from(
			new Set([...optimisticMatchingRemovedWorkerIds.value, ...workerIds])
		);

		const next = { ...optimisticRejectedProfiles.value };
		for (const profile of validProfiles) {
			next[profile.id] = profile;
		}
		optimisticRejectedProfiles.value = next;
	};

	const applyOptimisticGlobalRejection = (workerIds: string[]) => {
		const ids = workerIds.filter(Boolean);
		if (ids.length === 0) return;
		optimisticMatchingRemovedWorkerIds.value = Array.from(
			new Set([...optimisticMatchingRemovedWorkerIds.value, ...ids])
		);
	};

	const applyOptimisticRemoveFromAgency = (workerIds: string[]) => {
		const ids = workerIds.filter(Boolean);
		if (ids.length === 0) return;

		applyOptimisticGlobalRejection(ids);
		applyOptimisticRecruitmentRemoval(ids);

		const idsSet = new Set(ids);
		removePendingPoolWorkers(companyNeedId.value, (worker) =>
			idsSet.has(worker.workerId)
		);
		const removedPoolCount = paginatedPoolWorkers.value.items.filter((worker) =>
			idsSet.has(worker.workerId)
		).length;
		if (removedPoolCount === 0) return;

		paginatedPoolWorkers.value.items = paginatedPoolWorkers.value.items.filter(
			(worker) => !idsSet.has(worker.workerId)
		);
		optimisticPoolProfileDelta.value -= removedPoolCount;
	};

	const applyOptimisticCandidatesRemoval = (workerIds: string[]) => {
		const ids = workerIds.filter(Boolean);
		if (ids.length === 0) return;
		optimisticCandidatesRemovedWorkerIds.value = Array.from(
			new Set([...optimisticCandidatesRemovedWorkerIds.value, ...ids])
		);
	};

	const reconcileOptimisticCandidatesRemovals = (items: CandidateWorker[]) => {
		if (optimisticCandidatesRemovedWorkerIds.value.length === 0) return;
		const presentIds = new Set(items.map((candidate) => candidate.id));
		const remaining = optimisticCandidatesRemovedWorkerIds.value.filter(
			(workerId) => presentIds.has(workerId)
		);
		if (
			remaining.length !== optimisticCandidatesRemovedWorkerIds.value.length
		) {
			optimisticCandidatesRemovedWorkerIds.value = remaining;
		}
	};

	const applyOptimisticRecruitmentRemoval = (workerIds: string[]) => {
		const ids = workerIds.filter(Boolean);
		if (ids.length === 0) return;
		optimisticRecruitmentRemovedWorkerIds.value = Array.from(
			new Set([...optimisticRecruitmentRemovedWorkerIds.value, ...ids])
		);

		const next = { ...optimisticRecruitmentWorkers.value };
		for (const id of ids) {
			delete next[id];
		}
		optimisticRecruitmentWorkers.value = next;
	};

	const applyOptimisticPoolAddition = (count: number) => {
		if (count <= 0) return;
		optimisticPoolProfileDelta.value += count;
	};

	const addPendingPoolWorker = (needId: string, worker: WorkerPool) => {
		const pending = (pendingPoolWorkers.value[needId] ?? []).filter(
			(pendingWorker) => pendingWorker.workerId !== worker.workerId
		);
		pendingPoolWorkers.value = {
			...pendingPoolWorkers.value,
			[needId]: [worker, ...pending],
		};
	};

	const findRecruitmentWorkerById = (workerId: string) => {
		const profiles = recruitmentProfiles.value;
		if (!profiles) return undefined;

		return RECRUITMENT_STATUS_KEYS.flatMap((key) => profiles[key]).find(
			(recruitmentWorker) => recruitmentWorker.workerId === workerId
		);
	};

	const applySavedContact = (workerId: string, contact: WorkerContact) => {
		const optimisticWorker = optimisticRecruitmentWorkers.value[workerId];
		if (optimisticWorker) {
			optimisticWorker.worker.contact = contact;
		}

		const worker = findRecruitmentWorkerById(workerId);
		if (worker) worker.contact = contact;
	};

	const applyRescheduledInterview = (
		workerId: string,
		interview: WorkerInterview
	) => {
		const optimisticWorker = optimisticRecruitmentWorkers.value[workerId];
		if (optimisticWorker) {
			upsertWorkerInterview(optimisticWorker.worker, interview);
		}

		const worker = findRecruitmentWorkerById(workerId);
		if (worker) upsertWorkerInterview(worker, interview);
	};

	const applyJobOfferRepublishResponse = (
		jobOfferId: string,
		response: RepublishJobOfferResponse
	) => {
		if (
			!companyNeedData.value?.jobOffer ||
			companyNeedData.value.jobOffer.id !== jobOfferId
		)
			return;

		companyNeedData.value = {
			...companyNeedData.value,
			jobOffer: {
				...companyNeedData.value.jobOffer,
				status: response.status,
				...(response.status === JobOfferStatus.PUBLISHED
					? { publishedAt: Date.now(), hasRepublish: true }
					: {}),
			},
		};
	};

	const applyJobOfferStatus = (jobOfferId: string, status: JobOfferStatus) => {
		const currentJobOffer = companyNeedData.value?.jobOffer;
		if (!companyNeedData.value || currentJobOffer?.id !== jobOfferId) return;

		companyNeedData.value = {
			...companyNeedData.value,
			jobOffer: { ...currentJobOffer, status },
		};
	};

	const applyJobOfferDeletion = (
		jobOfferId: string
	): JobOfferDetails | null => {
		const currentJobOffer = companyNeedData.value?.jobOffer;
		if (!companyNeedData.value || currentJobOffer?.id !== jobOfferId)
			return null;

		companyNeedData.value = {
			...companyNeedData.value,
			jobOffer: undefined,
		};

		return currentJobOffer;
	};

	const restoreJobOffer = (jobOffer: JobOfferDetails) => {
		if (!companyNeedData.value) return;

		companyNeedData.value = { ...companyNeedData.value, jobOffer };
	};

	const addOptimisticMission = (mission: MissionList) => {
		const ids = new Set(optimisticMissionIds.value);
		ids.add(mission.id);
		optimisticMissionIds.value = ids;
		missions.value = [mission, ...missions.value];
		if (isCountedMissionStatus(mission.status))
			optimisticMissionsDelta.value += 1;
	};

	const reconcileOptimisticMission = (
		tempId: string,
		patch: Partial<MissionList> & { id: string }
	) => {
		const idx = missions.value.findIndex((m) => m.id === tempId);
		if (idx === -1) return;
		const next = [...missions.value];
		if (patch.status)
			optimisticMissionsDelta.value +=
				Number(isCountedMissionStatus(patch.status)) -
				Number(isCountedMissionStatus(next[idx].status));
		next[idx] = { ...next[idx], ...patch };
		missions.value = next;

		const ids = new Set(optimisticMissionIds.value);
		ids.delete(tempId);
		if (patch.id !== tempId) ids.add(patch.id);
		optimisticMissionIds.value = ids;
	};

	/**
	 * The optimistic row of a created mission has no actions until the query-side exposes it.
	 * Once the missions list returned the mission, its row already carries the server actions.
	 */
	const fillCreatedMissionActions = async (missionId: string) => {
		const details = await fetchMissionWhenProjected(missionId);
		if (!details || !optimisticMissionIds.value.has(missionId)) return;
		reconcileOptimisticMission(missionId, {
			id: missionId,
			availableActions: details.availableActions,
			states: details.states,
		});
	};

	const removeOptimisticMission = (tempId: string) => {
		const idx = missions.value.findIndex((m) => m.id === tempId);
		if (idx === -1) return;
		const removed = missions.value[idx];
		missions.value = missions.value.filter((m) => m.id !== tempId);

		const ids = new Set(optimisticMissionIds.value);
		ids.delete(tempId);
		optimisticMissionIds.value = ids;

		if (isCountedMissionStatus(removed.status))
			optimisticMissionsDelta.value -= 1;
	};

	/**
	 * Shifts the missions tab count when a displayed mission changes status. Call it
	 * before patching the status; returns its rollback.
	 */
	const shiftMissionsCountForStatus = (
		missionId: string,
		nextStatus: MissionStatus
	) => {
		const mission = missions.value.find((m) => m.id === missionId);
		if (!mission) return () => {};
		const shift =
			Number(isCountedMissionStatus(nextStatus)) -
			Number(
				isCountedMissionStatus(missionPatchStore.withPatch(mission).status)
			);
		optimisticMissionsDelta.value += shift;
		return () => {
			optimisticMissionsDelta.value -= shift;
		};
	};

	const applyAddedWorkerToMission = (
		missionId: string,
		worker: MissionBulkWorker,
		options?: {
			bumpMissionsListQuantity?: boolean;
			missionSummary?: { id: string; name: string; status: MissionStatus };
		}
	) => {
		if (options?.bumpMissionsListQuantity === false) return;
		const mission = missions.value.find((m) => m.id === missionId);
		if (mission) {
			setMissionListProposedQuantity(
				mission,
				getMissionListProposedQuantity(mission) + 1
			);
		} else {
			orphanProposedAdds.value[missionId] =
				(orphanProposedAdds.value[missionId] ?? 0) + 1;
		}
		if (options?.missionSummary) {
			applyOptimisticRecruitmentMissionProposedFromPool(
				worker,
				options.missionSummary
			);
		}
	};

	const removeWorkersFromPool = async (workerIds: string[]): Promise<void> => {
		const ids = new Set(workerIds);
		const removedCount = paginatedPoolWorkers.value.items.filter((worker) =>
			ids.has(worker.workerId)
		).length;
		const hasPendingToRemove = (
			pendingPoolWorkers.value[companyNeedId.value] ?? []
		).some((worker) => ids.has(worker.workerId));
		if (removedCount === 0 && !hasPendingToRemove) return;

		const previousPoolItems = paginatedPoolWorkers.value.items;
		const previousPoolTotal = paginatedPoolWorkers.value.total;
		const previousPoolProfileDelta = optimisticPoolProfileDelta.value;
		const previousPendingPoolWorkers = pendingPoolWorkers.value;

		removePendingPoolWorkers(companyNeedId.value, (worker) =>
			ids.has(worker.workerId)
		);

		paginatedPoolWorkers.value = {
			...paginatedPoolWorkers.value,
			items: paginatedPoolWorkers.value.items.filter(
				(worker) => !ids.has(worker.workerId)
			),
			total: Math.max(0, paginatedPoolWorkers.value.total - removedCount),
		};
		optimisticPoolProfileDelta.value -= removedCount;

		try {
			await companyNeedService.removeWorkersFromPool(
				companyNeedId.value,
				workerIds
			);
		} catch (error) {
			paginatedPoolWorkers.value = {
				...paginatedPoolWorkers.value,
				items: previousPoolItems,
				total: previousPoolTotal,
			};
			optimisticPoolProfileDelta.value = previousPoolProfileDelta;
			pendingPoolWorkers.value = previousPendingPoolWorkers;
			toastError(
				i18n.global.t('page.companyNeed.details.errors.removeFromPool')
			);
			throw error;
		}
	};

	const applyOptimisticMatchingWorkersToRecruitment = (
		workers: MoveWorker[],
		status: CompanyNeedWorkerStatus,
		createdCount = workers.length
	) => {
		const statusKey = WORKER_STATUS_TO_RECRUITMENT_KEY[status];
		if (!statusKey || workers.length === 0) return;

		const currentCount = recruitmentProfilesCount.value;

		const workerIds = Array.from(
			new Set(workers.map((worker) => worker.id).filter(Boolean))
		);
		optimisticMatchingRemovedWorkerIds.value = Array.from(
			new Set([...optimisticMatchingRemovedWorkerIds.value, ...workerIds])
		);

		const nextOptimisticWorkers = { ...optimisticRecruitmentWorkers.value };
		for (const worker of workers) {
			nextOptimisticWorkers[worker.id] = {
				worker: mapMoveWorkerToRecruitmentWorker(worker),
				statusKey,
			};
		}
		optimisticRecruitmentWorkers.value = nextOptimisticWorkers;

		if (createdCount > 0) {
			optimisticRecruitmentTargetCount.value = currentCount + createdCount;
		}
	};

	// Fetch
	const fetchCompanyNeed = async () => {
		if (!companyNeedId.value) return;
		await withLoading(
			CompanyNeedLoadingState.COMPANY_NEED,
			async () => {
				companyNeedData.value = await companyNeedService.fetchCompanyNeedById(
					companyNeedId.value
				);
				optimisticPoolProfileDelta.value = 0;
				optimisticMissionsDelta.value = 0;
				if (
					optimisticRecruitmentTargetCount.value !== null &&
					(companyNeedData.value?.ATSProfileNumber ?? 0) >=
						optimisticRecruitmentTargetCount.value
				) {
					optimisticRecruitmentTargetCount.value = null;
				}
			},
			'page.companyNeed.details.errors.fetchCompanyNeed'
		);
	};

	const buildMatchingBaseParams = (
		overrides: Pick<MatchingWorkersQueryParams, 'distanceKm'> = {}
	): Omit<
		MatchingWorkersQueryParams,
		'limit' | 'offset' | 'sortBy' | 'sortOrder' | 'keywords'
	> => ({
		search: matchingWorkersSearch.value || undefined,
		distanceKm: matchingDistanceKm.value ?? overrides.distanceKm,
		experienceLevels: matchingExperienceLevels.value?.length
			? matchingExperienceLevels.value
			: undefined,
		contractTypes: matchingContractTypes.value?.length
			? matchingContractTypes.value
			: undefined,
		lastUpdate: matchingLastUpdate.value,
		fullyAvailable: isMatchingAvailableOnly.value || undefined,
		hasJobOfferResponse: isMatchingJobOfferResponseOnly.value || undefined,
		hasOngoingProposal: matchingHasOngoingProposal.value,
		overrideJobIds: matchingQualification.value?.length
			? matchingQualification.value
			: undefined,
		excludedJobIds: matchingExcludedQualification.value?.length
			? matchingExcludedQualification.value
			: undefined,
		habilitations: matchingHabilitations.value?.length
			? matchingHabilitations.value
			: undefined,
		periodPreferences: matchingAvailabilities.value?.length
			? matchingAvailabilities.value.join(',')
			: undefined,
		weekEndRequired: matchingWeekEndRequired.value ?? false,
		workerTypes: matchingWorkerTypes.value?.length
			? matchingWorkerTypes.value
			: undefined,
	});

	const fetchMatchingWorkersMetadata = async () => {
		if (!companyNeedId.value) return;
		// Each facet ignores its own filter server-side, so both are sent to keep the other facet consistent.
		const params: MatchingWorkersMetadataQueryParams = {
			...buildMatchingBaseParams(),
			keywords: matchingKeywords.value,
			cities: matchingCities.value,
			flexible: isMatchingFlexibleEnabled.value || undefined,
		};
		// Skip the request when the metadata-relevant params have not changed.
		const paramsKey = `${companyNeedId.value}|${JSON.stringify(params)}`;
		if (paramsKey === lastMatchingMetadataParamsKey) return;
		try {
			matchingWorkersMetadata.value =
				await companyNeedService.fetchMatchingWorkersMetadataForCompanyNeed(
					companyNeedId.value,
					params
				);
			lastMatchingMetadataParamsKey = paramsKey;
		} catch (error) {
			// Metadata is best-effort: filter options remain the previously loaded list if the call fails.
			console.warn(
				'[companyNeed] Failed to fetch matching workers metadata',
				error
			);
		}
	};

	const fetchMatchingWorkers = async (
		params: MatchingWorkersQueryParams = {}
	) => {
		if (!companyNeedId.value) return;

		if (params.limit != null) {
			matchingWorkersLimit.value = params.limit;
		}

		const resolvedParams: MatchingWorkersQueryParams = {
			...params,
			limit: params.limit ?? matchingWorkersLimit.value,
			offset: params.offset ?? DEFAULT_MATCHING_OFFSET,
		};

		const isLatest = matchingWorkersRequests.start();
		setLoadingState(CompanyNeedLoadingState.MATCHING_WORKERS, true);
		try {
			const queryParams: MatchingWorkersQueryParams = {
				...resolvedParams,
				...buildMatchingBaseParams({ distanceKm: resolvedParams.distanceKm }),
				keywords: matchingKeywords.value?.length
					? matchingKeywords.value
					: undefined,
				cities: matchingCities.value?.length ? matchingCities.value : undefined,
				sortBy: resolvedParams.sortBy ?? matchingWorkersSortBy.value,
				sortOrder: resolvedParams.sortOrder ?? matchingWorkersSortOrder.value,
			};

			const matchingWorkers =
				await companyNeedService.fetchMatchingWorkersForCompanyNeed(
					companyNeedId.value,
					queryParams
				);
			if (!isLatest()) return;

			isMatchingFlexibleAvailable.value =
				matchingWorkers.total < DEFAULT_MATCHING_LIMIT;

			if (
				!isMatchingFlexibleAvailable.value &&
				isMatchingFlexibleEnabled.value
			) {
				isMatchingFlexibleEnabled.value = false;
			}

			const useFlexibleAggregation =
				isMatchingFlexibleEnabled.value && isMatchingFlexibleAvailable.value;

			if (!useFlexibleAggregation) {
				const formattedMatchingWorkers = formatMatchingWorkers(
					matchingWorkers.items,
					false
				);
				const pageLimit = resolvedParams.limit ?? matchingWorkersLimit.value;
				const currentPage =
					Math.floor((resolvedParams.offset ?? 0) / pageLimit) + 1;
				optimisticMatchingRemovedWorkerIds.value = [];
				paginatedMatchingWorkers.value = {
					...matchingWorkers,
					limit: pageLimit,
					offset: currentPage,
					items: formattedMatchingWorkers,
				};
				return;
			}

			isMatchingFlexibleLoading.value = true;
			await fetchMatchingWorkersFlexible(
				matchingWorkers,
				queryParams,
				resolvedParams
			);
		} catch {
			if (!isLatest()) return;
			toastError(
				i18n.global.t('page.companyNeed.details.errors.fetchMatchingWorkers')
			);
		} finally {
			if (isLatest()) {
				isMatchingFlexibleLoading.value = false;
				setLoadingState(CompanyNeedLoadingState.MATCHING_WORKERS, false);
			}
		}
	};

	const fetchCandidates = async (params: CandidatesQueryParams = {}) => {
		if (!companyNeedId.value || !hasJobOffer.value) return;

		if (params.limit != null) {
			candidatesLimit.value = params.limit;
		}

		const queryParams: CandidatesQueryParams = {
			...params,
			limit: params.limit ?? candidatesLimit.value,
			offset: params.offset ?? DEFAULT_CANDIDATES_OFFSET,
			search: candidatesSearch.value || undefined,
			jobIds: candidatesQualification.value?.length
				? candidatesQualification.value
				: undefined,
			originIds: candidatesOrigins.value?.length
				? candidatesOrigins.value
				: undefined,
			sortBy: params.sortBy ?? candidatesSortBy.value,
			sortOrder: params.sortOrder ?? candidatesSortOrder.value,
		};
		await withLoading(
			CompanyNeedLoadingState.CANDIDATES,
			async () => {
				const response = await companyNeedService.fetchCandidates(
					companyNeedId.value,
					queryParams
				);
				candidates.value = response.items;
				candidatesLimit.value = response.limit;
				candidatesPage.value = response.page;
				candidatesTotal.value = response.total;
				reconcileOptimisticCandidatesRemovals(response.items);
			},
			'page.companyNeed.details.errors.fetchCandidates'
		);
	};

	const fetchPoolWorkers = async (params: PoolWorkersQueryParams = {}) => {
		if (!companyNeedId.value) return;

		if (params.limit != null) {
			poolWorkersLimit.value = params.limit;
		}

		await withLoading(
			CompanyNeedLoadingState.POOL_WORKERS,
			async () => {
				const pageLimit = params.limit ?? poolWorkersLimit.value;
				const queryParams: PoolWorkersQueryParams = {
					...params,
					limit: pageLimit,
					offset: params.offset ?? DEFAULT_POOL_OFFSET,
					search: poolWorkersSearch.value || undefined,
					distance: poolDistanceKm.value,
					experienceLevels: poolExperienceLevels.value,
					fullyAvailable: isPoolAvailableOnly.value || undefined,
					lastUpdate: poolLastUpdate.value,
					contractTypes: poolContractTypes.value,
					hasOngoingProposal: poolHasOngoingProposal.value,
					jobIds: poolQualification.value?.length
						? poolQualification.value
						: undefined,
					excludedJobIds: poolExcludedQualification.value?.length
						? poolExcludedQualification.value
						: undefined,
				};
				const poolResponse = await companyNeedService.fetchPaginatedPoolWorkers(
					companyNeedId.value,
					queryParams
				);
				const currentPage =
					Math.floor((queryParams.offset ?? 0) / pageLimit) + 1;
				paginatedPoolWorkers.value = {
					...poolResponse,
					limit: pageLimit,
					offset: currentPage,
				};
				reconcilePendingPoolWorkers(companyNeedId.value, poolResponse.items);
			},
			'page.companyNeed.details.errors.fetchPoolWorkers'
		);
	};

	const fetchRejectedProfiles = async (search?: string) => {
		if (!companyNeedId.value) return;
		await withLoading(
			CompanyNeedLoadingState.REJECTED_PROFILES,
			async () => {
				rejectedProfiles.value = await companyNeedService.fetchRejectedProfiles(
					companyNeedId.value,
					search
				);
				reconcileOptimisticRejectedProfiles(rejectedProfiles.value);
			},
			'page.companyNeed.details.errors.fetchRejectedProfiles'
		);
	};

	// Latest request wins, so a filter changed while loading is never dropped
	const fetchMissions = async () => {
		if (!companyNeedId.value) return;
		const isLatest = missionsRequests.start();
		setLoadingState(CompanyNeedLoadingState.MISSIONS, true);
		try {
			const fresh = await missionService.fetchMissions(companyNeedId.value, {
				search: missionsSearch.value || undefined,
				status: missionsStatusFilter.value.length
					? missionsStatusFilter.value
					: undefined,
			});
			if (!isLatest()) return;
			missionPatchStore.reconcile(fresh);
			missions.value = mergeMissionsListWithLocalState(fresh);
		} catch {
			if (isLatest()) {
				toastError(
					i18n.global.t('page.companyNeed.details.errors.fetchMissions')
				);
			}
		} finally {
			if (isLatest()) {
				setLoadingState(CompanyNeedLoadingState.MISSIONS, false);
			}
		}
	};

	const fetchRecruitmentProfiles = async () => {
		if (!companyNeedId.value) return;
		await withLoading(
			CompanyNeedLoadingState.RECRUITMENT,
			async () => {
				recruitmentProfiles.value =
					await companyNeedService.fetchWorkersForCompanyNeed(
						companyNeedId.value,
						{}
					);
				reconcileOptimisticRecruitmentWorkers(recruitmentProfiles.value);
				reconcileOptimisticRecruitmentRemovals(recruitmentProfiles.value);
			},
			'page.companyNeed.details.errors.fetchRecruitmentProfiles'
		);
	};

	const fetchCompanyContacts = async () => {
		if (!companyNeedId.value || !companyNeedCompanyId.value) return;
		await withLoading(
			CompanyNeedLoadingState.COMPANY_CONTACTS,
			async () => {
				companyContacts.value = await clientService.fetchCompanyContacts(
					// biome-ignore lint/style/noNonNullAssertion: Init Biome
					companyNeedCompanyId.value!
				);
			},
			'page.companyNeed.details.errors.fetchCompanyContacts'
		);
	};

	// Orchestration
	const fetchAllData = async () => {
		restoreMatchingFilters();
		restorePoolFilters();
		await Promise.all([
			fetchCompanyNeed(),
			fetchMatchingWorkers(),
			fetchRecruitmentProfiles(),
		]);
		await Promise.all([
			hasJobOffer.value ? fetchCandidates() : Promise.resolve(),
			fetchCompanyContacts(),
		]);
	};

	const reloadData = async (sections: CompanyNeedReloadSection[]) => {
		const reloadMap: Record<CompanyNeedReloadSection, () => Promise<void>> = {
			companyNeed: fetchCompanyNeed,
			matchingWorkers: fetchMatchingWorkers,
			candidates: fetchCandidates,
			poolWorkers: fetchPoolWorkers,
			rejectedProfiles: fetchRejectedProfiles,
			missions: fetchMissions,
			recruitment: fetchRecruitmentProfiles,
			companyContacts: fetchCompanyContacts,
		};

		await Promise.all(sections.map((section) => reloadMap[section]()));
	};

	// Le matching dépend de la géolocalisation du besoin ; en CQRS la projection query
	// est asynchrone, d'où le délai avant de recharger la liste.
	const MATCHING_REFRESH_DELAY_MS = 1500;
	let matchingRefreshTimeout: ReturnType<typeof setTimeout> | null = null;

	const refreshAfterCompanyNeedUpdate = async () => {
		await fetchCompanyNeed();

		if (route.name !== ROUTES.APP.COMPANY_NEED.DETAIL.MATCHING) return;

		if (matchingRefreshTimeout) clearTimeout(matchingRefreshTimeout);
		matchingRefreshTimeout = setTimeout(() => {
			matchingRefreshTimeout = null;
			if (route.name !== ROUTES.APP.COMPANY_NEED.DETAIL.MATCHING) return;
			fetchMatchingWorkers();
		}, MATCHING_REFRESH_DELAY_MS);
	};

	// ─── Return ──────────────────────────────────────────────────────────────

	return {
		// Core
		companyNeedId,
		companyNeed,
		companyNeedCompanyId,
		companyContacts,
		actionBarSlot,
		setActionBarSlot,
		getLoadingState,
		setLoadingState,
		counts,

		// Matching
		matchingWorkersSearch,
		matchingWorkersFilters,
		isMatchingAvailableOnly,
		isMatchingJobOfferResponseOnly,
		isMatchingFlexibleEnabled,
		isMatchingFlexibleAvailable,
		isMatchingFlexibleLoading,
		matchingWorkersSortBy,
		matchingWorkersSortOrder,
		matchingWorkersLimit,
		setMatchingFlexibleEnabled,
		paginatedMatchingWorkers,
		visibleMatchingWorkers,
		matchingWorkersMetadata,
		fetchMatchingWorkers,
		fetchMatchingWorkersMetadata,

		// Pool
		poolWorkersSearch,
		poolWorkersFilters,
		isPoolAvailableOnly,
		poolWorkersLimit,
		paginatedPoolWorkers,
		visiblePoolWorkers,
		fetchPoolWorkers,
		addPendingPoolWorker,

		// Rejected profiles
		rejectedProfiles,
		visibleRejectedProfiles,
		fetchRejectedProfiles,

		// Candidates
		candidates,
		visibleCandidates,
		visibleCandidatesTotal,
		candidatesSearch,
		candidatesFilters,
		candidatesLimit,
		candidatesPage,
		candidatesTotal,
		candidatesSortBy,
		candidatesSortOrder,
		fetchCandidates,

		// Missions
		missions,
		missionsSearch,
		missionsStatusFilter,
		optimisticMissionsDelta,
		optimisticMissionIds,
		addOptimisticMission,
		reconcileOptimisticMission,
		fillCreatedMissionActions,
		removeOptimisticMission,
		shiftMissionsCountForStatus,
		fetchMissions,

		// Recruitment
		recruitmentProfiles,
		mergedRecruitmentProfiles,
		fetchRecruitmentProfiles,

		// Optimistic updates (state refs are exposed so Pinia DevTools shows reactive updates)
		optimisticMatchingRemovedWorkerIds,
		optimisticCandidatesRemovedWorkerIds,
		optimisticPoolProfileDelta,
		optimisticRecruitmentRemovedWorkerIds,
		optimisticRecruitmentWorkers,
		optimisticRejectedProfiles,
		optimisticRecruitmentTargetCount,
		applyOptimisticForNeedRejection,
		applyOptimisticGlobalRejection,
		applyOptimisticRemoveFromAgency,
		applyOptimisticCandidatesRemoval,
		applyOptimisticRecruitmentRemoval,
		applyOptimisticPoolAddition,
		applySavedContact,
		applyRescheduledInterview,
		applyJobOfferRepublishResponse,
		applyJobOfferStatus,
		applyJobOfferDeletion,
		restoreJobOffer,
		applyOptimisticMatchingWorkersToRecruitment,
		applyAddedWorkerToMission,
		removeWorkersFromPool,

		// Company
		fetchCompanyNeed,
		fetchCompanyContacts,

		// Reset
		resetMatchingWorkersState,
		resetPoolWorkersState,
		resetRejectedProfilesState,
		resetMissionsState,
		resetRecruitmentState,
		resetCompanyContactsState,
		resetDetailsState,

		// Orchestration
		fetchAllData,
		reloadData,
		refreshAfterCompanyNeedUpdate,
	};
});
